import React, { useState, useCallback } from 'react';
import { Link, useLocation, useParams } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useCampaign } from '../../context/CampaignContext';
import { t } from '../../lib/i18n';
import { cn, isGameMaster } from '../../lib/utils';
import { ScrollArea } from '../ui/scroll-area';
import {
  LayoutDashboard,
  Scroll,
  Calendar,
  Compass,
  Users,
  Map,
  Gem,
  Swords,
  MessageSquare,
  Settings,
  ChevronLeft,
  ChevronRight,
  Book,
  ClipboardList,
  ScrollText,
  History,
  Network,
  GitBranch,
  Package,
  Navigation,
  PanelLeftClose,
  PanelLeft,
} from 'lucide-react';

/* ────────────────────────────────────────────
   Constants
──────────────────────────────────────────── */
const WIDTH_PINNED   = 240;
const WIDTH_COMPACT  = 52;

/* ────────────────────────────────────────────
   SidebarTooltip — floating label per icon
   Uses React state for reliable hover detection
──────────────────────────────────────────── */
const SidebarTooltip = ({ children, label }) => {
  const [hovered, setHovered] = React.useState(false);

  return (
    <div
      className="relative"
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      {children}
      <div
        className="absolute left-full top-1/2 -translate-y-1/2 ml-2 z-50 pointer-events-none"
        style={{
          opacity: hovered ? 1 : 0,
          transform: `translateY(-50%) translateX(${hovered ? '0px' : '-6px'})`,
          transition: 'opacity 0.15s ease-out, transform 0.15s ease-out',
        }}
      >
        <div className="relative flex items-center">
          {/* Arrow */}
          <div
            className="absolute -left-[5px] top-1/2 -translate-y-1/2"
            style={{ width: 6, height: 10, overflow: 'hidden' }}
          >
            <div
              className="bg-popover border border-border/60"
              style={{
                width: 8,
                height: 8,
                transform: 'rotate(45deg) translate(3px, 1px)',
              }}
            />
          </div>
          {/* Label bubble */}
          <span className="relative block px-2.5 py-1.5 rounded-md bg-popover border border-border/60 shadow-lg shadow-black/10 text-xs font-medium text-popover-foreground whitespace-nowrap">
            {label}
          </span>
        </div>
      </div>
    </div>
  );
};

/* ────────────────────────────────────────────
   Sidebar
──────────────────────────────────────────── */
export const Sidebar = ({
  campaignId,
  variant = 'desktop',
  className,
  scrollAreaClassName
}) => {
  const { language, user } = useAuth();
  const { currentCampaign } = useCampaign();
  const location = useLocation();
  const params = useParams();

  const activeId = campaignId || params.campaignId;
  const userRole = currentCampaign?.members?.find(m => m.user_id === user?.id)?.role;
  const isMJ = isGameMaster(userRole);

  /* ── Pin state (persisted) ── */
  const [isPinned, setIsPinned] = useState(() => {
    try { return localStorage.getItem('sidebar-pinned') === 'true'; } catch { return false; }
  });

  const togglePin = useCallback(() => {
    setIsPinned(prev => {
      const next = !prev;
      try { localStorage.setItem('sidebar-pinned', String(next)); } catch {}
      window.dispatchEvent(new Event('sidebar-pinned-change'));
      return next;
    });
  }, []);

  /* ── Navigation sections ── */
  const navSections = activeId && isMJ ? [
    {
      title: language === 'fr' ? 'Exploration' : 'Exploration',
      items: [
        { label: t('nav_dashboard', language), href: `/campaigns/${activeId}`,          icon: LayoutDashboard, exact: true },
        { label: t('nav_sessions',  language), href: `/campaigns/${activeId}/sessions`, icon: Calendar },
      ]
    },
    {
      title: language === 'fr' ? 'Aventure' : 'Adventure',
      items: [
        { label: t('nav_travel',    language), href: `/campaigns/${activeId}/travel`,    icon: Navigation },
        { label: t('nav_quests',    language), href: `/campaigns/${activeId}/quests`,    icon: Compass },
        { label: t('nav_combat',    language), href: `/campaigns/${activeId}/combat`,    icon: Swords },
        { label: t('nav_narrative', language), href: `/campaigns/${activeId}/narrative`, icon: GitBranch },
      ]
    },
    {
      title: language === 'fr' ? 'Univers' : 'World',
      items: [
        { label: t('nav_npcs',    language),                          href: `/campaigns/${activeId}/npcs`,     icon: Users },
        { label: t('nav_treasure', language),                         href: `/campaigns/${activeId}/treasure`, icon: Gem },
        { label: t('nav_maps',    language),                          href: `/campaigns/${activeId}/maps`,     icon: Map },
        { label: language === 'fr' ? 'Encyclopédie' : 'Encyclopedia', href: `/campaigns/${activeId}/lore`,     icon: Book },
        { label: language === 'fr' ? 'Chronologie'  : 'Timeline',     href: `/campaigns/${activeId}/timeline`, icon: History },
        { label: language === 'fr' ? 'Relations'    : 'Relations',    href: `/campaigns/${activeId}/graph`,    icon: Network },
      ]
    },
    {
      title: language === 'fr' ? 'Outils MJ' : 'GM Tools',
      items: [
        { label: t('nav_gm_characters', language), href: `/campaigns/${activeId}/characters`, icon: ScrollText },
        { label: t('nav_settings',      language), href: `/campaigns/${activeId}/settings`,   icon: Settings },
      ]
    },
  ] : activeId ? [
    {
      title: language === 'fr' ? 'Exploration' : 'Exploration',
      items: [
        { label: t('nav_dashboard', language), href: `/campaigns/${activeId}`,          icon: LayoutDashboard, exact: true },
        { label: t('nav_sessions',  language), href: `/campaigns/${activeId}/sessions`, icon: Calendar },
      ]
    },
    {
      title: language === 'fr' ? 'Aventure' : 'Adventure',
      items: [
        { label: t('nav_quests',          language), href: `/campaigns/${activeId}/quests`,          icon: Compass },
        { label: t('nav_character_sheet', language), href: `/campaigns/${activeId}/character-sheet`, icon: ClipboardList },
        { label: t('nav_inventory',       language), href: `/campaigns/${activeId}/inventory`,       icon: Package },
      ]
    },
    {
      title: language === 'fr' ? 'Univers' : 'World',
      items: [
        { label: t('nav_npcs',  language),                            href: `/campaigns/${activeId}/npcs`,     icon: Users },
        { label: t('nav_maps',  language),                            href: `/campaigns/${activeId}/maps`,     icon: Map },
        { label: language === 'fr' ? 'Encyclopédie' : 'Encyclopedia', href: `/campaigns/${activeId}/lore`,     icon: Book },
        { label: language === 'fr' ? 'Chronologie'  : 'Timeline',     href: `/campaigns/${activeId}/timeline`, icon: History },
        { label: language === 'fr' ? 'Relations'    : 'Relations',    href: `/campaigns/${activeId}/graph`,    icon: Network },
      ]
    },
  ] : null;

  const isActive = (href, exact = false) =>
    exact ? location.pathname === href : location.pathname.startsWith(href);

  /* ─── Mobile variant (unchanged — full sidebar in Sheet) ─── */
  const isMobile = variant === 'mobile';
  if (isMobile) {
    return (
      <aside className={cn("border-r border-border bg-card block", className)} data-testid="sidebar">
        {currentCampaign && (
          <div className="shrink-0 px-3 pt-5 pb-2.5 border-b border-border">
            <Link
              to="/campaigns"
              className="flex items-center gap-1.5 text-[11px] text-muted-foreground hover:text-foreground mb-1.5 group"
              data-testid="back-to-campaigns"
            >
              <ChevronLeft className="h-3 w-3 group-hover:-translate-x-1 transition-transform" />
              {t('nav_campaigns', language)}
            </Link>
            <h2 className="font-cinzel font-bold text-sm truncate" data-testid="sidebar-campaign-name">
              {currentCampaign.name}
            </h2>
            {userRole && (
              <span className={cn(
                "text-[10px] px-1.5 py-0.5 rounded-full mt-1 inline-block",
                isMJ ? "bg-primary/10 text-primary" : "bg-secondary text-secondary-foreground"
              )}>
                {t(`campaign_role_${userRole}`, language)}
              </span>
            )}
          </div>
        )}
        <ScrollArea className={scrollAreaClassName || "flex-1 min-h-0"}>
          <div className="p-2.5 space-y-3">
            {navSections ? navSections.map((section, sIdx) => (
              <div key={section.title} className={sIdx > 0 ? "pt-2" : undefined}>
                <p className="font-semibold text-muted-foreground/70 uppercase tracking-widest text-[9px] px-2.5 mb-0.5">
                  {section.title}
                </p>
                <nav className="space-y-0.5">
                  {section.items.map((item) => {
                    const Icon = item.icon;
                    const active = isActive(item.href, item.exact);
                    return (
                      <Link
                        key={item.href}
                        to={item.href}
                        className={cn(
                          "sidebar-nav-item flex items-center gap-2 py-1.5 px-2.5 rounded-md text-xs text-muted-foreground",
                          "hover:bg-secondary hover:text-foreground transition-colors",
                          active && "active"
                        )}
                      >
                        <Icon className="h-3.5 w-3.5 shrink-0" />
                        <span>{item.label}</span>
                      </Link>
                    );
                  })}
                </nav>
              </div>
            )) : (
              <Link to="/dashboard" className="sidebar-nav-item flex items-center gap-2 py-1.5 px-2.5 rounded-md text-xs text-muted-foreground hover:bg-secondary hover:text-foreground transition-colors">
                <LayoutDashboard className="h-3.5 w-3.5 shrink-0" />
                <span>{t('nav_dashboard', language)}</span>
              </Link>
            )}
          </div>
        </ScrollArea>
      </aside>
    );
  }

  /* ═══════════════════════════════════════════════════
     Desktop variant
  ═══════════════════════════════════════════════════ */

  const showTooltips = !isPinned; // tooltips only when compact

  /* ── Render a single nav item ── */
  const renderNavItem = (item) => {
    const Icon = item.icon;
    const active = isActive(item.href, item.exact);

    const linkEl = (
      <Link
        to={item.href}
        className={cn(
          "sidebar-nav-item flex items-center rounded-md transition-all duration-150",
          isPinned
            ? "h-9 px-3 gap-2.5 text-[13px]"
            : "h-9 w-9 mx-auto justify-center",
          "text-muted-foreground",
          "hover:bg-secondary/80 hover:text-foreground",
          active && "sidebar-active"
        )}
        data-testid={`sidebar-${item.label.toLowerCase().replace(/\s/g, '-')}`}
      >
        <Icon className={cn(
          "shrink-0 transition-transform duration-150",
          isPinned ? "h-4 w-4" : "h-[18px] w-[18px]"
        )} />
        {isPinned && (
          <span className="truncate leading-none">{item.label}</span>
        )}
      </Link>
    );

    if (showTooltips) {
      return (
        <SidebarTooltip key={item.href} label={item.label} show>
          {linkEl}
        </SidebarTooltip>
      );
    }

    return <React.Fragment key={item.href}>{linkEl}</React.Fragment>;
  };

  return (
    <aside
      className={cn(
        "hidden lg:flex flex-col shrink-0 border-r border-border bg-card/95 backdrop-blur-sm",
        "transition-[width,min-width] duration-300 ease-[cubic-bezier(0.4,0,0.2,1)]",
        className
      )}
      style={{
        width:    isPinned ? WIDTH_PINNED : WIDTH_COMPACT,
        minWidth: isPinned ? WIDTH_PINNED : WIDTH_COMPACT,
      }}
      data-testid="sidebar"
    >
      {/* ── Header ── */}
      <div className="shrink-0 border-b border-border/50">
        {isPinned ? (
          /* Pinned header — full campaign info */
          <div className="px-3 py-3">
            {currentCampaign && (
              <>
                <Link
                  to="/campaigns"
                  className="flex items-center gap-1 text-[11px] text-muted-foreground hover:text-foreground group mb-0.5"
                  data-testid="back-to-campaigns"
                >
                  <ChevronLeft className="h-3 w-3 shrink-0 group-hover:-translate-x-0.5 transition-transform" />
                  <span className="truncate">{t('nav_campaigns', language)}</span>
                </Link>
                <p className="font-cinzel font-bold text-sm truncate" data-testid="sidebar-campaign-name">
                  {currentCampaign.name}
                </p>
                {userRole && (
                  <span className={cn(
                    "text-[10px] px-1.5 py-0.5 rounded-full mt-1 inline-block",
                    isMJ ? "bg-primary/10 text-primary" : "bg-secondary text-secondary-foreground"
                  )}>
                    {t(`campaign_role_${userRole}`, language)}
                  </span>
                )}
              </>
            )}
            <button
              onClick={togglePin}
              className="absolute top-3 right-3 p-1 rounded-md text-muted-foreground/50 hover:text-foreground hover:bg-secondary transition-colors"
              title={language === 'fr' ? 'Réduire le menu' : 'Collapse sidebar'}
            >
              <PanelLeftClose className="h-4 w-4" />
            </button>
          </div>
        ) : (
          /* Compact header — just the pin/expand button */
          <div className="flex justify-center py-2.5">
            <button
              onClick={togglePin}
              className={cn(
                "p-2 rounded-md transition-all duration-150",
                "text-muted-foreground/60 hover:text-foreground hover:bg-secondary"
              )}
              title={language === 'fr' ? 'Fixer le menu ouvert' : 'Pin sidebar open'}
            >
              <PanelLeft className="h-4 w-4" />
            </button>
          </div>
        )}
      </div>

      {/* ── Navigation ── */}
      {(() => {
        const navContent = (
          <div className={cn(
            "py-2",
            isPinned ? "px-2 space-y-1" : "px-1.5 space-y-0.5"
          )}>
            {navSections ? navSections.map((section, sIdx) => (
              <div key={section.title} className={sIdx > 0 ? "pt-1" : undefined}>
                {/* Section title — only when pinned */}
                {isPinned && (
                  <p className="font-semibold text-muted-foreground/50 uppercase tracking-widest text-[10px] px-3 pt-2 pb-1">
                    {section.title}
                  </p>
                )}
                {/* Compact separator */}
                {!isPinned && sIdx > 0 && (
                  <div className="flex justify-center py-1.5">
                    <div className="w-4 h-px bg-border/60 rounded-full" />
                  </div>
                )}
                <nav className="space-y-0.5">
                  {section.items.map(renderNavItem)}
                </nav>
              </div>
            )) : (
              <nav>{renderNavItem({ label: t('nav_dashboard', language), href: '/dashboard', icon: LayoutDashboard, exact: true })}</nav>
            )}
          </div>
        );

        /* When compact, avoid ScrollArea (overflow:hidden clips tooltips) */
        if (!isPinned) {
          return <div className="flex-1">{navContent}</div>;
        }
        return <ScrollArea className="flex-1">{navContent}</ScrollArea>;
      })()}
    </aside>
  );
};

export default Sidebar;
